import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as userService from '../../services/userService';

const UserForm = ({ user, onSave, onCancel, loading, isModal = false }) => {
  const { userRole } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    email: '',
    role: 'CLIENT',
    coach_id: '',
    password: '',
    confirm_password: '',
  });
  const [coaches, setCoaches] = useState([]);
  const [errors, setErrors] = useState({});
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  // Fetch coaches when component mounts
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const { data, error } = await userService.getCoaches();
        if (error) {
          console.error('Error fetching coaches:', error);
        } else {
          setCoaches(data || []);
        }
      } catch (err) {
        console.error('Error fetching coaches:', err);
      }
    };

    fetchCoaches();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        full_name: user.full_name || '',
        email: user.email || '',
        role: user.role || 'CLIENT',
        coach_id: user.coach_id || '',
        password: '',
        confirm_password: '',
      });
      setShowPasswordFields(false);
    } else {
      // Reset form when adding a new user
      setFormData({
        first_name: '',
        last_name: '',
        full_name: '',
        email: '',
        role: 'CLIENT',
        coach_id: '',
        password: '',
        confirm_password: '',
      });
      setShowPasswordFields(true); // Always show for new users if we want to allow setting password (though currently it auto-generates)
    }
    // Clear errors when user changes
    setErrors({});
  }, [user]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // For new users, ensure password will be generated
    if (!user && (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim())) {
      newErrors.general = 'All required fields must be filled for new users';
    }

    // Password validation
    if (formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: value,
      };

      // Auto-generate full_name when first_name or last_name changes
      if (name === 'first_name' || name === 'last_name') {
        const firstName = name === 'first_name' ? value : prev.first_name;
        const lastName = name === 'last_name' ? value : prev.last_name;
        newFormData.full_name = `${firstName} ${lastName}`.trim();
      }

      return newFormData;
    });

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    let submissionData = { ...formData };

    // If it's a new user, generate a password if not provided
    if (!user) {
      // If password is provided manually, use it. Otherwise generate one.
      // Note: Currently the UI for new user doesn't show password fields by default in the same way,
      // but if we wanted to allow setting initial password, we could.
      // For now, we'll stick to the existing behavior of generating one if not provided.
      
      const passwordToUse = submissionData.password || Math.random().toString(36).slice(-12);
      submissionData.password = passwordToUse;
      
      // Remove confirm_password from submission
      delete submissionData.confirm_password;
      
      try {
        await onSave(submissionData);
        if (!formData.password) {
          alert(`User added successfully! Generated password: ${passwordToUse}`);
        }
      } catch (error) {
        console.error("Failed to save user:", error);
        setErrors({ general: error.message || "Failed to save user. Please try again." });
      }
    } else {
      // For existing users
      // Remove confirm_password from submission
      delete submissionData.confirm_password;
      
      // If password is empty, remove it so we don't try to update it with empty string
      if (!submissionData.password) {
        delete submissionData.password;
      }

      try {
        await onSave(submissionData);
      } catch (error) {
        console.error("Failed to save user:", error);
        setErrors({ general: error.message || "Failed to save user. Please try again." });
      }
    }
  };

  return (
    <div className={isModal ? '' : 'bg-white shadow rounded-lg p-4'}>
      {!isModal && <h2 className="text-xl font-bold mb-4">{user ? 'Edit User' : 'Add User'}</h2>}
      
      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              id="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                errors.first_name ? 'border-red-500' : ''
              }`}
              required
            />
            {errors.first_name && (
              <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
            )}
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="last_name"
              id="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                errors.last_name ? 'border-red-500' : ''
              }`}
              required
            />
            {errors.last_name && (
              <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">Full Name</label>
          <input
            type="text"
            name="full_name"
            id="full_name"
            value={formData.full_name}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-gray-50"
            placeholder="Auto-generated from first and last name"
            readOnly
          />
          <p className="mt-1 text-xs text-gray-500">This field is automatically generated from first and last name</p>
        </div>

        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
              errors.email ? 'border-red-500' : ''
            }`}
            required
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="CLIENT">CLIENT</option>
            <option value="COACH">COACH</option>
            <option value="SUPER_ADMIN">SUPER_ADMIN</option>
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="coach_id" className="block text-sm font-medium text-gray-700">Assign Coach (Optional)</label>
          <select
            id="coach_id"
            name="coach_id"
            value={formData.coach_id}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">No coach assigned</option>
            {coaches.map((coach) => (
              <option key={coach.id} value={coach.id}>
                {coach.first_name && coach.last_name
                  ? `${coach.first_name} ${coach.last_name}`
                  : coach.full_name || coach.email}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">Select a coach to assign this user to</p>
        </div>

        {/* Password Change Section - Only for Super Admins editing existing users */}
        {user && userRole === 'SUPER_ADMIN' && (
          <div className="mb-6 border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
              <button
                type="button"
                onClick={() => setShowPasswordFields(!showPasswordFields)}
                className="text-sm text-indigo-600 hover:text-indigo-900"
              >
                {showPasswordFields ? 'Cancel Password Change' : 'Change Password'}
              </button>
            </div>

            {showPasswordFields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    id="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.password ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter new password"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    name="confirm_password"
                    id="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.confirm_password ? 'border-red-500' : ''
                    }`}
                    placeholder="Confirm new password"
                  />
                  {errors.confirm_password && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">
                    Note: Changing the password will immediately affect the user's ability to log in.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 disabled:opacity-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Saving...' : (user ? 'Update User' : 'Save User')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;